import { KeyboardEventHandler, useLayoutEffect, useRef } from "react";
import Button from "../Button";
import { SendIcon, SpinningLoaderIcon } from "../fancyIcons";
import useSwipeToTargetRef from "../../../hooks/useSwipeToTarget";
import classNames from "classnames";

const ConversationChatBox = ({
  message,
  placeholder,
  onMessageChange,
  onSend,
  isSendingMessage,
  disabled,
  sendDisabled,
  handleFocus,
  AttachmentsComponent,
}: {
  message: string;
  placeholder: string;
  onMessageChange: (message: string) => void;
  onSend: () => void;
  isSendingMessage: boolean;
  disabled: boolean;
  sendDisabled: boolean;
  handleFocus?: () => void;
  AttachmentsComponent?: React.ReactNode;
}) => {
  const textAreaKeyListener: KeyboardEventHandler<HTMLTextAreaElement> = (
    e,
  ) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      onSend();
    }
  };

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const closeKeyboard = () => {
    textareaRef.current?.blur();
  };

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // If we're growing, this can be skipped. Nice nugget to make the transition animation work :)
      if (textarea.scrollHeight <= textarea.clientHeight) {
        textarea.style.height = "inherit";
      }

      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [textareaRef, message]);

  // This ref is used to detect a swipe gesture downward onto the chat box to close the keyboard on mobile.
  const targetRef = useSwipeToTargetRef(closeKeyboard);

  return (
    <div ref={targetRef} className={styles.container}>
      <textarea
        ref={textareaRef}
        className={styles.textArea}
        value={message}
        placeholder={placeholder}
        onKeyDown={textAreaKeyListener}
        onChange={(e) => onMessageChange(e.target.value)}
        onFocus={handleFocus}
        disabled={disabled}
        rows={2}
        data-testid="chat-input"
      />
      <div className="tw-flex tw-flex-row tw-items-end tw-justify-between">
        {AttachmentsComponent}
        <div className="tw-ml-auto">
          <Button
            type="primary"
            disabled={sendDisabled || isSendingMessage}
            Icon={isSendingMessage ? SpinningLoaderIcon : SendIcon}
            onClick={onSend}
            data-testid={
              isSendingMessage ? "send-button-loading" : "send-button"
            }
          />
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: classNames(
    "tw-group",
    "tw-flex tw-flex-col",
    "tw-gap-1",
    "tw-rounded-md tw-border",
    "tw-border-neutral-300",
    "tw-bg-neutral-50",
    "tw-p-2",
    "tw-transition-all",
    "focus-within:tw-border-fay-500 focus-within:tw-bg-white",
  ),

  textArea: classNames(
    "tw-max-h-[40vh]",
    "tw-resize-none",
    "tw-bg-transparent",
    "tw-outline-none",
    "tw-transition-all",
  ),
};

export default ConversationChatBox;
